'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { FeedCard } from '@/domains/paydirt/layout/FeedCard';
import { paydirtConfig } from '@/config/paydirt';
import { listClaims, type ClaimSummary } from '@/domains/paydirt/claims/api';

const PAGE_SIZE = 12;

type Props = {
  query?: string;
  userId?: string;
  showHeading?: boolean;
};

export function HaulReportsFeed({ query, userId, showHeading = true }: Props) {
  const [claims, setClaims] = useState<ClaimSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const inFlightRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadPage = useCallback(
    async (cursor?: string) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const isInitial = !cursor;
      if (isInitial) setLoading(true);
      else setLoadingMore(true);
      try {
        const data = await listClaims({
          status: 'published',
          limit: PAGE_SIZE,
          cursor,
          q: query?.trim() || undefined,
          userId: userId?.trim() || undefined,
        });
        const batch = data.claims ?? [];
        setClaims((prev) => (isInitial ? batch : [...prev, ...batch]));
        setNextCursor(data.nextCursor);
      } catch {
        if (isInitial) setClaims([]);
        setNextCursor(undefined);
      } finally {
        inFlightRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [query, userId],
  );

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !nextCursor || loading || loadingMore || query?.trim()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadPage(nextCursor);
        }
      },
      { rootMargin: '240px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, loading, loadingMore, loadPage, query]);

  const emptyMessage = query?.trim()
    ? 'No haul reports match your search.'
    : 'No published claims yet. Be the first to share your haul.';

  return (
    <section
      className="social-feed"
      role="list"
      aria-label="Recent haul reports"
      aria-busy={loading || loadingMore}
      data-testid="haul-reports-feed"
    >
      {showHeading && <h2>Recent haul reports</h2>}
      {loading ? (
        <p className="social-feed__status text-muted" role="status">
          Loading haul reports…
        </p>
      ) : claims.length === 0 ? (
        <div className="feed-card feed-card--empty">
          <p className="text-muted">{emptyMessage}</p>
          <Link href="/claims/new/" className="btn btn-primary">
            {paydirtConfig.claimCtaLabel}
          </Link>
        </div>
      ) : (
        <>
          {claims.map((c) => (
            <div key={c.id} role="listitem">
              <FeedCard
                id={c.id}
                title={c.title}
                userId={c.userId}
                roiPct={c.roiPct}
                createdAt={c.createdAt}
                communityApproved={c.communityApproved}
                communityValidity={c.communityValidity}
                hasVideo={c.hasVideo}
              />
            </div>
          ))}
          {!query?.trim() && <div ref={sentinelRef} className="social-feed__sentinel" aria-hidden />}
          {loadingMore && (
            <p className="social-feed__status text-muted" role="status">
              Loading more…
            </p>
          )}
        </>
      )}
    </section>
  );
}
